import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { Language, TranslateService } from '@ngx-translate/core';
import { LanguageAvailable } from '@shared/services/language/models';
import { LogsData } from '@shared/services/logs';
import { map, Observable, of } from 'rxjs';

/**
 * Reactive holder of the last language explicitly requested by the app.
 *
 * @remarks Updated by {@link LanguageData.use}. It is initialized with an asserted `undefined`
 * value; consumers should not read it before the first successful language selection.
 */
export const CURRENT_LANGUAGE = signal<LanguageAvailable>(undefined!);

/**
 * Reactive holder of the primary (base) language code derived from {@link CURRENT_LANGUAGE}.
 *
 * @example
 * // When CURRENT_LANGUAGE is 'fr-FR', PRIMARY_CURRENT_LANGUAGE is 'fr'
 */
export const PRIMARY_CURRENT_LANGUAGE = signal<string>(undefined!);

@Injectable({
  providedIn: 'root',
})
export class LanguageData {
  private readonly translateService = inject(TranslateService);
  private readonly logs = inject(LogsData);
  private readonly document: Document = inject(DOCUMENT);

  /**
   * Switches the active application language and updates related reactive state.
   *
   * Attempts to change the current language via `TranslateService.use(...)`. If the requested language
   * is already active, returns an observable that synchronously emits the current language and completes.
   * Also updates {@link CURRENT_LANGUAGE}, {@link PRIMARY_CURRENT_LANGUAGE}, the `<html lang>` attribute,
   * and emits an informational log entry.
   *
   * @param language The BCP 47 locale to activate (for example, `'fr-FR'`). Should be one of the supported languages.
   * @returns Observable that emits the activated language once the underlying translation resources are loaded.
   * @example
   * languageService.use('fr-FR').subscribe((lang) => {
   *   console.log('Language set to', lang);
   * });
   * @see TranslateService#use
   */
  use(language: LanguageAvailable): Observable<LanguageAvailable> {
    const current = this.translateService.getCurrentLang() as LanguageAvailable;
    if (current === language) {
      return of(current);
    }
    CURRENT_LANGUAGE.set(language);
    this.updateDocumentLanguage(language);
    this.logs.info('LanguageService', `Changing language to "${language}"`);
    return this.translateService.use(language).pipe(map(() => language));
  }

  /**
   * Performs a synchronous translation lookup for the provided key(s).
   *
   * Delegates to `TranslateService.instant(...)` to immediately resolve a translation string or
   * object without waiting for asynchronous resource loading. Prefer this for imperative code paths
   * where translations are already available; in templates, prefer the translate pipe.
   *
   * @param key A translation key or an array of keys to resolve.
   * @param interpolateParams Optional parameters used to interpolate placeholders within the translation value.
   * @returns The resolved translation value(s). When `key` is a string, returns a single value; when `key` is an array, returns a map of key to value.
   * @example
   * const title = languageService.instant('home.title');
   * const withParams = languageService.instant('greeting', { name: 'Socooc' });
   * @see TranslateService#instant
   */
  instant(key: string | string[], interpolateParams?: Record<string, unknown>): string {
    return this.translateService.instant(key, interpolateParams) as string;
  }

  /**
   * Synchronizes the document's `<html lang>` attribute with the active language.
   *
   * @param language The BCP 47 language tag to apply to the document element.
   * @internal Invoked by {@link use} to keep the DOM in sync with the translation state.
   */
  private updateDocumentLanguage(language: Language): void {
    const current = this.document.documentElement.lang;
    if (current !== language) {
      this.document.documentElement.lang = language;
    }
  }
}
